import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Link } from "wouter";
import {
  Shield, UserPlus, Phone, Radio, Pencil, Trash2,
  ArrowLeft, Users, MessageSquare, CheckCircle2, XCircle, Clock
} from "lucide-react";
import CommandNav from "@/components/CommandNav";

const statusLabels: Record<string, string> = {
  available: "Disponível",
  busy: "Ocupado",
  offline: "Offline",
};

const statusColors: Record<string, string> = {
  available: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  busy: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  offline: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
};

const statusIcons: Record<string, React.ReactNode> = {
  available: <CheckCircle2 className="w-3 h-3" />,
  busy: <Clock className="w-3 h-3" />,
  offline: <XCircle className="w-3 h-3" />,
};

const channelLabels: Record<string, string> = {
  sms: "SMS",
  whatsapp: "WhatsApp",
};

function AgentFormDialog({ agent, onClose }: { agent?: any; onClose: () => void }) {
  const [fullName, setFullName] = useState(agent?.fullName || "");
  const [phone, setPhone] = useState(agent?.phone || "");
  const [role, setRole] = useState(agent?.role || "");
  const [status, setStatus] = useState(agent?.status || "available");
  const [channel, setChannel] = useState(agent?.channel || "sms");
  const [notes, setNotes] = useState(agent?.notes || "");

  const utils = trpc.useUtils();
  const createMut = trpc.agent.create.useMutation({
    onSuccess: () => { utils.agent.list.invalidate(); toast.success("Agente cadastrado"); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  const updateMut = trpc.agent.update.useMutation({
    onSuccess: () => { utils.agent.list.invalidate(); toast.success("Agente atualizado"); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  const validatePhone = (p: string) => {
    const cleaned = p.replace(/[^0-9+]/g, '');
    // Accept: +5511999407145, 5511999407145, 11999407145, 999407145
    return cleaned.length >= 10 && cleaned.length <= 15;
  };

  const handleSubmit = () => {
    if (!fullName.trim()) { toast.error("Nome é obrigatório"); return; }
    if (!phone.trim()) { toast.error("Telefone é obrigatório para acionamento"); return; }
    if (!validatePhone(phone)) { toast.error("Telefone inválido. Use formato: +55 11 99999-9999"); return; }
    const data = { fullName, phone, role: role || undefined, status: status as any, channel: channel as any, notes: notes || undefined };
    if (agent) {
      updateMut.mutate({ id: agent.id, ...data });
    } else {
      createMut.mutate(data);
    }
  };

  const loading = createMut.isPending || updateMut.isPending;

  return (
    <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
      <DialogHeader>
        <DialogTitle className="text-lg font-semibold flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-400" />
          {agent ? "Editar Agente" : "Novo Agente"}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4 pt-2">
        <div>
          <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome Completo *</Label>
          <Input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Nome do agente" className="bg-zinc-800 border-zinc-700 text-white mt-1" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Telefone *</Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+55 11 99999-9999" className="bg-zinc-800 border-zinc-700 text-white mt-1" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Função</Label>
            <Input value={role} onChange={e => setRole(e.target.value)} placeholder="Segurança, Steward..." className="bg-zinc-800 border-zinc-700 text-white mt-1" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="available">Disponível</SelectItem>
                <SelectItem value="busy">Ocupado</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Canal</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="sms">SMS</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-zinc-400 text-xs uppercase tracking-wider">Observações</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notas sobre o agente..." className="bg-zinc-800 border-zinc-700 text-white mt-1 min-h-[60px]" />
        </div>

        <Button onClick={handleSubmit} disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700">
          {loading ? "Salvando..." : agent ? "Atualizar" : "Cadastrar Agente"}
        </Button>
      </div>
    </DialogContent>
  );
}

export default function Agents() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAgent, setEditAgent] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [expandedAgentId, setExpandedAgentId] = useState<number | null>(null);

  const { data: agents, isLoading } = trpc.agent.list.useQuery();
  const utils = trpc.useUtils();
  const deleteMut = trpc.agent.delete.useMutation({
    onSuccess: () => { utils.agent.list.invalidate(); toast.success("Agente removido"); },
    onError: (e) => toast.error(e.message),
  });

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (!agents) return;
    const filtered = agents.filter(a => !statusFilter || a.status === statusFilter);
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(a => a.id)));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Remover ${selectedIds.size} agente(s) selecionado(s)?`)) return;
    const ids = Array.from(selectedIds);
    for (const id of ids) {
      try {
        await deleteMut.mutateAsync({ id });
      } catch (e) {
        // continue deleting others
      }
    }
    setSelectedIds(new Set());
    setSelectMode(false);
    utils.agent.list.invalidate();
  };

  const availableCount = agents?.filter(a => a.status === "available").length || 0;
  const busyCount = agents?.filter(a => a.status === "busy").length || 0;
  const offlineCount = agents?.filter(a => a.status === "offline").length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white">
      {/* Header — ELLA Command Platform */}
      <div className="sticky top-0 z-50">
        <CommandNav
          pageTitle="Agentes de Campo"
          rightContent={
            <div className="flex items-center gap-2">
              {agents && agents.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className={`text-xs border-zinc-700 ${selectMode ? 'bg-red-900/30 border-red-700 text-red-400' : 'text-zinc-300 hover:bg-zinc-800'}`}
                  onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }}
                >
                  {selectMode ? <><XCircle className="w-3.5 h-3.5 mr-1" /> Cancelar</> : <><Trash2 className="w-3.5 h-3.5 mr-1" /> Selecionar</>}
                </Button>
              )}
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditAgent(null); }}>
                <DialogTrigger asChild>
                  <Button size="sm" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 40%, #ec4899 100%)" }} className="text-white border-0 hover:opacity-90 text-sm">
                    <UserPlus className="w-4 h-4 mr-1" /> Novo Agente
                  </Button>
                </DialogTrigger>
                <AgentFormDialog agent={editAgent} onClose={() => { setDialogOpen(false); setEditAgent(null); }} />
              </Dialog>
            </div>
          }
        />
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className={`bg-zinc-900/50 border-zinc-800 cursor-pointer transition-all hover:border-purple-500/50 ${statusFilter === null ? 'ring-1 ring-purple-500/50' : ''}`} onClick={() => setStatusFilter(null)}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{agents?.length || 0}</p>
                <p className="text-xs text-zinc-500">Total de Agentes</p>
              </div>
            </CardContent>
          </Card>
          <Card className={`bg-zinc-900/50 border-zinc-800 cursor-pointer transition-all hover:border-emerald-500/50 ${statusFilter === 'available' ? 'ring-1 ring-emerald-500/50' : ''}`} onClick={() => setStatusFilter(statusFilter === 'available' ? null : 'available')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-400">{availableCount}</p>
                <p className="text-xs text-zinc-500">Disponíveis</p>
              </div>
            </CardContent>
          </Card>
          <Card className={`bg-zinc-900/50 border-zinc-800 cursor-pointer transition-all hover:border-amber-500/50 ${statusFilter === 'busy' ? 'ring-1 ring-amber-500/50' : ''}`} onClick={() => setStatusFilter(statusFilter === 'busy' ? null : 'busy')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{busyCount}</p>
                <p className="text-xs text-zinc-500">Ocupados</p>
              </div>
            </CardContent>
          </Card>
          <Card className={`bg-zinc-900/50 border-zinc-800 cursor-pointer transition-all hover:border-zinc-500/50 ${statusFilter === 'offline' ? 'ring-1 ring-zinc-500/50' : ''}`} onClick={() => setStatusFilter(statusFilter === 'offline' ? null : 'offline')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-zinc-500/20 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-zinc-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-zinc-400">{offlineCount}</p>
                <p className="text-xs text-zinc-500">Offline</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Batch Delete Bar */}
        {selectMode && (
          <div className="flex items-center gap-4 p-3 rounded-lg bg-red-900/20 border border-red-800/40">
            <Checkbox
              checked={agents ? selectedIds.size === agents.filter(a => !statusFilter || a.status === statusFilter).length && selectedIds.size > 0 : false}
              onCheckedChange={() => selectAll()}
              className="border-red-500 data-[state=checked]:bg-red-600"
            />
            <span className="text-sm text-red-300">
              {selectedIds.size > 0 ? `${selectedIds.size} agente(s) selecionado(s)` : "Selecione os agentes para remover"}
            </span>
            <div className="flex-1" />
            {selectedIds.size > 0 && (
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white text-xs cursor-pointer"
                onClick={deleteSelected}
                disabled={deleteMut.isPending}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                {deleteMut.isPending ? "Removendo..." : `Remover ${selectedIds.size}`}
              </Button>
            )}
          </div>
        )}

        {/* Agent List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full" />
          </div>
        ) : !agents || agents.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-12 text-center">
              <Shield className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400 text-lg mb-2">Nenhum agente cadastrado</p>
              <p className="text-zinc-500 text-sm mb-4">Cadastre agentes de campo para acionamento em eventos operacionais</p>
              <Button onClick={() => setDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700">
                <UserPlus className="w-4 h-4 mr-1" /> Cadastrar Primeiro Agente
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {statusFilter && (
              <div className="flex items-center gap-2 mb-4">
                <Badge className={`text-xs ${statusColors[statusFilter]} border`}>
                  {statusIcons[statusFilter]} {statusLabels[statusFilter]}
                </Badge>
                <button onClick={() => setStatusFilter(null)} className="text-xs text-zinc-500 hover:text-white transition-colors">
                  Limpar filtro
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {agents.filter(a => !statusFilter || a.status === statusFilter).map((agent) => (
              <Card
                key={agent.id}
                className={`bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer ${selectMode && selectedIds.has(agent.id) ? 'ring-2 ring-red-500/60 border-red-700' : ''} ${expandedAgentId === agent.id ? 'ring-1 ring-purple-500/50 border-purple-700' : ''}`}
                onClick={selectMode ? () => toggleSelect(agent.id) : () => setExpandedAgentId(expandedAgentId === agent.id ? null : agent.id)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {selectMode && (
                        <Checkbox
                          checked={selectedIds.has(agent.id)}
                          onCheckedChange={() => toggleSelect(agent.id)}
                          className="border-red-500 data-[state=checked]:bg-red-600"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-red-600 flex items-center justify-center text-white font-bold text-sm">
                        {agent.fullName.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-white text-sm">{agent.fullName}</p>
                        {agent.role && <p className="text-xs text-zinc-400">{agent.role}</p>}
                      </div>
                    </div>
                    <Badge className={`text-[10px] px-2 py-0.5 border ${statusColors[agent.status]} flex items-center gap-1`}>
                      {statusIcons[agent.status]}
                      {statusLabels[agent.status]}
                    </Badge>
                  </div>

                  <div className="space-y-2 mb-4">
                    {agent.phone && (
                      <div className="flex items-center gap-2 text-xs text-zinc-400">
                        <Phone className="w-3.5 h-3.5" />
                        <span>{agent.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                      <MessageSquare className="w-3.5 h-3.5" />
                      <span>Canal: {channelLabels[agent.channel]}</span>
                    </div>
                    {agent.notes && (
                      <p className="text-xs text-zinc-500 italic line-clamp-2">{agent.notes}</p>
                    )}
                  </div>

                  {/* V10.1: Expanded details when clicked */}
                  {expandedAgentId === agent.id && !selectMode && (
                    <div className="mb-4 p-3 bg-zinc-800/50 rounded-lg space-y-3">
                      <p className="text-xs text-zinc-300 font-medium">Ações Rápidas:</p>
                      <div className="flex flex-wrap gap-2">
                        {agent.phone && (
                          <>
                            <Button
                              size="sm"
                              className="text-xs bg-green-700 hover:bg-green-600 text-white cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); window.open(`https://wa.me/${(agent.phone || '').replace(/\D/g, '')}`, '_blank'); }}
                            >
                              <MessageSquare className="w-3 h-3 mr-1" /> WhatsApp
                            </Button>
                            <Button
                              size="sm"
                              className="text-xs bg-blue-700 hover:bg-blue-600 text-white cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); window.location.href = `sms:${(agent.phone || '').replace(/\D/g, '')}`; }}
                            >
                              <MessageSquare className="w-3 h-3 mr-1" /> SMS
                            </Button>
                            <Button
                              size="sm"
                              className="text-xs bg-purple-700 hover:bg-purple-600 text-white cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${(agent.phone || '').replace(/\D/g, '')}`; }}
                            >
                              <Phone className="w-3 h-3 mr-1" /> Ligar
                            </Button>
                          </>
                        )}
                      </div>
                      {agent.notes && (
                        <div className="text-xs text-zinc-400">
                          <span className="font-medium text-zinc-300">Observações:</span> {agent.notes}
                        </div>
                      )}
                      <div className="flex gap-2 pt-2 border-t border-zinc-700">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800 cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); setEditAgent(agent); setDialogOpen(true); }}
                        >
                          <Pencil className="w-3 h-3 mr-1" /> Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs border-red-800/50 text-red-400 hover:bg-red-900/30 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`Remover agente ${agent.fullName}?`)) deleteMut.mutate({ id: agent.id });
                          }}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Excluir
                        </Button>
                      </div>
                    </div>
                  )}

                  {!selectMode && (
                    <div className="flex items-center gap-2 pt-3 border-t border-zinc-800">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800 cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); setEditAgent(agent); setDialogOpen(true); }}
                      >
                        <Pencil className="w-3 h-3 mr-1" /> Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs border-red-800/50 text-red-400 hover:bg-red-900/30 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm("Remover este agente?")) deleteMut.mutate({ id: agent.id });
                        }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            </div>
          </>
        )}

        {/* Info Banner */}
        <Card className="bg-purple-900/20 border-purple-800/30">
          <CardContent className="p-4 flex items-start gap-3">
            <Radio className="w-5 h-5 text-purple-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-purple-200 font-medium">Acionamento Automático</p>
              <p className="text-xs text-purple-300/70 mt-1">
                Quando um evento operacional é criado, o sistema aciona automaticamente o agente disponível mais próximo via WhatsApp ou SMS. 
                Configure o telefone e canal preferido de cada agente para habilitar o acionamento direto.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
